import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@features/auth/context/AuthContext';
import { DndContext, useSensor, useSensors, PointerSensor, DragOverlay } from '@dnd-kit/core';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { getApiUrl } from '@/config';
import type { Technician, ViewMode, Assignment } from '@shared/types';
import { Header } from '@shared/components/Layout/Header';
import { MonthView } from '../components/Calendar/MonthView';
import { YearView } from '../components/Calendar/YearView';
import { AssignmentModal } from '../components/AssignmentModal';
import { addDays, differenceInCalendarDays, parseISO, format } from 'date-fns';

import { AssignmentsPage } from './AssignmentsPage';
import { MapPage } from './MapPage';
import { PendingClosures } from '@features/admin/components/PendingClosures';
import { TechnicianAssignmentView } from '../components/Assignment/TechnicianAssignmentView';
import { EditAssignmentModal } from '../components/Assignment/EditAssignmentModal';
import { motion } from 'framer-motion';
import { AssignmentBacklog } from '../components/Planning/AssignmentBacklog';
import { Briefcase, CalendarDays, MapPin, AlertCircle, X, ArrowLeft, Clock } from 'lucide-react';
import { useLayout } from '@shared/contexts/LayoutContext';
import { PlanningToolbar } from '../components/Planning/PlanningToolbar';


const INITIAL_TECHNICIANS: Technician[] = [
    { id: '1', name: 'Laura', service: 'Maintenance', color: '#f472b6', overrides: {}, assignments: [] },
    { id: '2', name: 'Thomas', service: 'Maintenance', color: '#22d3ee', overrides: {}, assignments: [] },
    { id: '3', name: 'Marie', service: 'Reseau', color: '#a78bfa', overrides: {}, assignments: [] },
    { id: '4', name: 'Sophie', service: 'Électricité', color: '#fb923c', overrides: {}, assignments: [] },
    { id: '5', name: 'Julien', service: 'Plomberie', color: '#34d399', overrides: {}, assignments: [] },
    { id: '6', name: 'Camille', service: 'Climatisation', color: '#60a5fa', overrides: {}, assignments: [] },
    { id: '7', name: 'Marc', service: 'Maintenance', color: '#f87171', overrides: {}, assignments: [] },
    { id: '8', name: 'Léa', service: 'Reseau', color: '#c084fc', overrides: {}, assignments: [] }
];

interface PlanningDashboardProps {
    initialTechnicians?: Technician[];
    onUpdateTechnician: (techId: string, updates: Partial<Technician>) => void;
    onCloseWorksite: (assignmentId: string) => void;
    onStartWorksite: (assignmentId: string) => void;
    onFinishWorksite: (assignmentId: string) => void;
    socketId?: string | null;
}

const generateWorksiteId = (targetDateStr: string): string => {
    return `CH-${format(parseISO(targetDateStr), 'yyMM')}-${Math.floor(1000 + Math.random() * 9000)}`;
};

export const PlanningDashboard: React.FC<PlanningDashboardProps> = ({ initialTechnicians = INITIAL_TECHNICIANS, onUpdateTechnician, onCloseWorksite, onStartWorksite, onFinishWorksite, socketId }) => {
    // const { logout } = useAuth(); 
    const navigate = useNavigate();
    const { layoutMode } = useLayout();
    const { user } = useAuth();
    
    const canWrite = user?.role === 'admin' || 
                    (user?.permissions?.planning === 'write') || 
                    (user?.role === 'planner' && !user?.permissions);
    const [currentDate, setCurrentDate] = useState(new Date(2026, 0, 1));
    const [viewMode, setViewMode] = useState<ViewMode>('month');
    const [technicians, setTechnicians] = useState<Technician[]>(initialTechnicians);
    const [activeDragItem, setActiveDragItem] = useState<any>(null);
    const [backlogVersion, setBacklogVersion] = useState(0);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        })
    );

    const handleDragStart = (event: DragStartEvent) => {
        if (!canWrite) return;
        setActiveDragItem(event.active.data.current);
    };

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        setActiveDragItem(null);

        if (!over || !canWrite) return;

        // Extract Tech and Date from Droppable data (Robust method)
        // Extraction des données de la cible (Cellule)
        let targetTechId: string | undefined;
        let targetDateStr: string | undefined;

        // Méthode 1: Via les data (Plus robuste)
        const overData = over.data.current;
        if (overData && overData.techId && overData.date) {
            targetTechId = overData.techId;
            // Parse la date ISO en YYYY-MM-DD
            try {
                targetDateStr = format(parseISO(overData.date), 'yyyy-MM-dd');
            } catch (e) {
                console.error("Erreur parsing date overData", e);
            }
        }

        // Méthode 2: Fallback via ID (Parsing manuel si data manquant)
        if (!targetTechId || !targetDateStr) {
            const overId = over.id ? String(over.id) : '';
            // Format Attendu: cell-TECHID-YYYY-MM-DD
            if (overId && overId.startsWith('cell-')) {
                const parts = (overId || '').split('-');
                if (parts.length >= 5) { // au moins cell, tech, Y, M, D
                    targetDateStr = parts.slice(parts.length - 3).join('-');
                    targetTechId = parts.slice(1, parts.length - 3).join('-');
                }
            }
        }

        if (!targetTechId || !targetDateStr) {
            console.warn("Impossible de déterminer la cible du drop", over);
            return;
        }

        // --- CASE 1: BACKLOG DROP ---
        if (active.id.toString().startsWith('backlog-')) {
            const assignmentData = active.data.current?.assignment;
            const assignmentId = assignmentData?.id || active.id.toString().replace('backlog-', '');

            if (!assignmentId) return;

            try {
                // Generate ID
                const candidateWorksiteId = generateWorksiteId(targetDateStr);

                const token = localStorage.getItem('token');
                const res = await fetch(getApiUrl(`/api/assignments/${assignmentId}`), {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                        'x-socket-id': socketId || ''
                    },
                    body: JSON.stringify({
                        technicianId: targetTechId,
                        startDate: targetDateStr,
                        endDate: targetDateStr,
                        status: 'todo',
                        worksiteId: candidateWorksiteId
                    })
                });

                if (res.ok) {
                    const rawAssignment = await res.json();

                    // Convert Backend Assignment to Frontend
                    const normalizedAssignment: Assignment = {
                        id: rawAssignment.id,
                        worksiteId: rawAssignment.worksiteId || candidateWorksiteId,
                        clientName: rawAssignment.clientName,
                        startDate: targetDateStr, // Explicitly set from drop target
                        endDate: targetDateStr,   // Explicitly set from drop target
                        address: rawAssignment.address,
                        city: rawAssignment.city,
                        postalCode: rawAssignment.postalCode,
                        status: 'todo',
                        task: rawAssignment.type,
                        type: rawAssignment.type,
                        remarque: rawAssignment.notes,
                        color: '#3b82f6',
                    };

                    // Update local state
                    handleUpdateTechnicianLocal(targetTechId, {
                        assignments: [
                            ...(technicians.find(t => t.id === targetTechId)?.assignments || []),
                            normalizedAssignment
                        ]
                    });

                    // Trigger Backlog Refresh
                    setBacklogVersion(v => v + 1);
                }
            } catch (e) {
                console.error('Backlog drop error', e);
            }

            // --- CASE 2: INTERNAL MOVE ---
        } else if (active.id.toString().startsWith('assignment-')) {
            const activeData = active.data.current;
            if (!activeData) return;

            const { assignment, techId: sourceTechId, segmentDate: isoSegmentDate } = activeData;

            // Calculate Delta
            const segmentDate = parseISO(isoSegmentDate);
            const targetDate = parseISO(targetDateStr);
            const diffDays = differenceInCalendarDays(targetDate, segmentDate);

            if (sourceTechId === targetTechId && diffDays === 0) return; // No change

            const newStartDate = addDays(parseISO(assignment.startDate), diffDays);
            const newEndDate = addDays(parseISO(assignment.endDate), diffDays);

            const updatedStart = format(newStartDate, 'yyyy-MM-dd');
            const updatedEnd = format(newEndDate, 'yyyy-MM-dd');
            const updatedAssignment = { ...assignment, startDate: updatedStart, endDate: updatedEnd };

            try {
                const token = localStorage.getItem('token');
                fetch(getApiUrl(`/api/assignments/${assignment.id}`), {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                        'x-socket-id': socketId || ''
                    },
                    body: JSON.stringify({
                        technicianId: targetTechId,
                        startDate: updatedStart,
                        endDate: updatedEnd
                    })
                });

                // Optimistic Update
                const sourceTech = technicians.find(t => t.id === sourceTechId);
                const targetTech = technicians.find(t => t.id === targetTechId);

                if (!sourceTech || !targetTech) return;

                const newSourceAssignments = sourceTech.assignments.filter(a => a.id !== assignment.id);

                // If source and target are same, we use the already filtered list as base
                // If different, we use target's existing list
                const newTargetAssignments = (sourceTechId === targetTechId)
                    ? [...newSourceAssignments, updatedAssignment]
                    : [...targetTech.assignments, updatedAssignment];

                setTechnicians(prev => {
                    return prev.map(t => {
                        if (t.id === sourceTechId && sourceTechId !== targetTechId) {
                            return { ...t, assignments: newSourceAssignments };
                        }
                        if (t.id === targetTechId) {
                            return { ...t, assignments: newTargetAssignments };
                        }
                        return t;
                    });
                });

                // Sync with Parent
                if (sourceTechId !== targetTechId) {
                    onUpdateTechnician(sourceTechId, { assignments: newSourceAssignments });
                    onUpdateTechnician(targetTechId, { assignments: newTargetAssignments });
                } else {
                    onUpdateTechnician(targetTechId, { assignments: newTargetAssignments });
                }
            } catch (e) {
                console.error("Move error", e);
            }
        }
    };

    // Sync state with props
    useEffect(() => {
        setTechnicians(initialTechnicians);
    }, [initialTechnicians]);

    // Filtering State
    const [selectedService, setSelectedService] = useState<string>('Tous');
    const [showWeekends, setShowWeekends] = useState(true);
    const [showBacklog, setShowBacklog] = useState(true);

    // Standalone Assignment Modal
    const [showStandaloneAssignment, setShowStandaloneAssignment] = useState(false);
    const [activeTab, setActiveTab] = useState<'planning' | 'assignments' | 'map' | 'closures'>('planning');
    const [selectedAssignmentId, setSelectedAssignmentId] = useState<string | null>(null);
    const [editingAssignment, setEditingAssignment] = useState<{ assignment: Assignment; techIds: string[] } | null>(null);

    // Find selected assignment from any technician
    const selectedAssignment = selectedAssignmentId
        ? technicians.flatMap(t => t.assignments).find(a => a.id === selectedAssignmentId) || null
        : null;

    // Derive unique services
    const services = Array.from(new Set(technicians.map(t => t.service))).sort();

    const filteredTechnicians = technicians.filter(tech => {
        if (tech.archived) return false;
        if (selectedService !== 'Tous' && tech.service !== selectedService) return false;
        return true;
    });

    const handleUpdateTechnicianLocal = (techId: string, updates: Partial<Technician>) => {
        setTechnicians(prevTechs => prevTechs.map(tech =>
            tech.id === techId ? { ...tech, ...updates } : tech
        ));
        onUpdateTechnician(techId, updates);
    };

    const handleEditAssignment = (assignment: Assignment) => {
        const techIds = technicians
            .filter(t => t.assignments.some(a => a.id === assignment.id))
            .map(t => t.id);
        setEditingAssignment({ assignment, techIds });
        setShowStandaloneAssignment(false);
    };

    const handleStartWorksiteLocal = (assignmentId: string) => {
        setTechnicians(prevTechs =>
            prevTechs.map(tech => ({
                ...tech,
                assignments: tech.assignments.map(a =>
                    a.id === assignmentId
                        ? { ...a, status: 'in-progress', startedAt: new Date().toISOString() }
                        : a
                )
            }))
        );
        onStartWorksite(assignmentId);
    };

    const handleFinishWorksiteLocal = (assignmentId: string) => {
        setTechnicians(prevTechs =>
            prevTechs.map(tech => ({
                ...tech,
                assignments: tech.assignments.map(a =>
                    a.id === assignmentId
                        ? { ...a, status: 'completed-by-tech', completedAt: new Date().toISOString() }
                        : a
                )
            }))
        );
        onFinishWorksite(assignmentId);
    };

    const handleCloseWorksiteLocal = (assignmentId: string) => {
        setTechnicians(prevTechs =>
            prevTechs.map(tech => ({
                ...tech,
                assignments: tech.assignments.map(a =>
                    a.id === assignmentId
                        ? { ...a, status: 'closed', closedAt: new Date().toISOString() }
                        : a
                )
            }))
        );
        onCloseWorksite(assignmentId);
    };

    const handleSaveEdit = async (updatedAssignment: Assignment, newTechIds: string[]) => {
        if (!editingAssignment) return;

        const oldTechIds = editingAssignment.techIds;
        const assignmentId = updatedAssignment.id;

        // Logic shared with AssignmentsPage - ideally refactor to hook/helper
        // Remove from old
        oldTechIds.forEach(techId => {
            if (!newTechIds.includes(techId)) {
                const tech = technicians.find(t => t.id === techId);
                if (tech) {
                    const newAssignments = tech.assignments.filter(a => a.id !== assignmentId);
                    handleUpdateTechnicianLocal(techId, { assignments: newAssignments });
                }
            }
        });

        // Add/Update new
        newTechIds.forEach(techId => {
            const tech = technicians.find(t => t.id === techId);
            if (tech) {
                const existingIndex = tech.assignments.findIndex(a => a.id === assignmentId);
                let newAssignments;
                if (existingIndex >= 0) {
                    newAssignments = [...tech.assignments];
                    newAssignments[existingIndex] = updatedAssignment;
                } else {
                    newAssignments = [...tech.assignments, updatedAssignment];
                }
                handleUpdateTechnicianLocal(techId, { assignments: newAssignments });
            }
        });

        // Persist to Backend (Canonical Source)
        try {
            const token = localStorage.getItem('token');
            const primaryTechId = newTechIds.length > 0 ? newTechIds[0] : null;

            const payload = {
                ...updatedAssignment,
                technicianId: primaryTechId
            };

            await fetch(getApiUrl(`/api/assignments/${assignmentId}`), {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });
        } catch (error) {
            console.error('Failed to save assignment changes to backend:', error);
        }

        setEditingAssignment(null);
    };

    const handleDeleteAssignment = () => {
        if (!editingAssignment) return;
        const assignmentId = editingAssignment.assignment.id;
        editingAssignment.techIds.forEach(techId => {
            const tech = technicians.find(t => t.id === techId);
            if (tech) {
                const newAssignments = tech.assignments.filter(a => a.id !== assignmentId);
                handleUpdateTechnicianLocal(techId, { assignments: newAssignments });
            }
        });
        setEditingAssignment(null);
    };

    return (
        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <div className="min-h-screen p-4 md:p-6 max-w-[95%] mx-auto">
                {/* Admin Header with Tabs */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                    <div className="flex items-center gap-4">
                        {layoutMode !== 'sidebar' && (
                            <button
                                onClick={() => navigate('/')}
                                className="p-2 hover:bg-[var(--button-hover-bg)] rounded-full transition-colors text-muted hover:text-main"
                                title="Retour au Tableau de Bord"
                            >
                                <ArrowLeft size={24} />
                            </button>
                        )}
                        <div>
                            <h1 className="text-2xl font-bold text-main flex items-center gap-3">
                                <CalendarDays className="text-primary" />
                                Planning & Interventions
                            </h1>
                            <p className="text-sm text-muted">Vue complète de tous les techniciens</p>
                        </div>
                    </div>

                    {/* Tab Navigation - Now in Header */}
                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={() => setActiveTab('planning')}
                            className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${activeTab === 'planning'
                                ? 'bg-primary/20 text-primary border border-primary/50'
                                : 'bg-[var(--button-bg)] text-muted border border-[var(--button-border)] hover:bg-[var(--button-hover-bg)]'
                                }`}
                        >
                            <CalendarDays size={18} />
                            <span>Planning</span>
                        </button>
                        <button
                            onClick={() => setActiveTab('assignments')}
                            className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${activeTab === 'assignments'
                                ? 'bg-accent/20 text-accent border border-accent/50'
                                : 'bg-[var(--button-bg)] text-muted border border-[var(--button-border)] hover:bg-[var(--button-hover-bg)]'
                                }`}
                        >
                            <Briefcase size={18} />
                            <span>Chantiers</span>
                        </button>
                        <button
                            onClick={() => setActiveTab('map')}
                            className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${activeTab === 'map'
                                ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                                : 'bg-[var(--button-bg)] text-muted border border-[var(--button-border)] hover:bg-[var(--button-hover-bg)]'
                                }`}
                        >
                            <MapPin size={18} />
                            <span>Carte</span>
                        </button>
                        <button
                            onClick={() => setActiveTab('closures')}
                            className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${activeTab === 'closures'
                                ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50'
                                : 'bg-[var(--button-bg)] text-muted border border-[var(--button-border)] hover:bg-[var(--button-hover-bg)]'
                                }`}
                        >
                            <AlertCircle size={18} />
                            <span>Clôtures</span>
                        </button>
                    </div>
                </div>

                {activeTab === 'planning' ? (
                    <>
                        <div className="flex h-[calc(100vh-200px)] gap-4">
                            {/* Backlog Sidebar */}
                            {showBacklog && (
                                <motion.div
                                    initial={{ width: 0, opacity: 0 }}
                                    animate={{ width: 'auto', opacity: 1 }}
                                    exit={{ width: 0, opacity: 0 }}
                                    className="hidden lg:block h-full"
                                >
                                    <AssignmentBacklog version={backlogVersion} />
                                </motion.div>
                            )}

                            {/* Main Planning Area */}
                            <div className="flex-1 flex flex-col min-w-0">
                                {layoutMode === 'sidebar' ? (
                                    <PlanningToolbar
                                        currentDate={currentDate}
                                        setCurrentDate={setCurrentDate}
                                        viewMode={viewMode}
                                        setViewMode={setViewMode}
                                        services={services}
                                        selectedService={selectedService}
                                        setSelectedService={setSelectedService}
                                        showWeekends={showWeekends}
                                        setShowWeekends={setShowWeekends}
                                        onNewAssignment={canWrite ? () => setShowStandaloneAssignment(true) : undefined}
                                        showBacklog={showBacklog}
                                        setShowBacklog={setShowBacklog}
                                    />
                                ) : (
                                    <Header
                                        currentDate={currentDate}
                                        setCurrentDate={setCurrentDate}
                                        viewMode={viewMode}
                                        setViewMode={setViewMode}
                                        services={services}
                                        selectedService={selectedService}
                                        setSelectedService={setSelectedService}
                                        showWeekends={showWeekends}
                                        setShowWeekends={setShowWeekends}
                                        onNewAssignment={canWrite ? () => setShowStandaloneAssignment(true) : undefined}
                                    />
                                )}

                                <main className="flex-1 overflow-y-auto mt-4">
                                    {viewMode === 'month' || viewMode === 'week' ? (
                                        <MonthView
                                            currentDate={currentDate}
                                            technicians={filteredTechnicians}
                                            showWeekends={showWeekends}
                                            onUpdateTechnician={handleUpdateTechnicianLocal}
                                            viewMode={viewMode}
                                            allTechnicians={technicians.filter(t => !t.archived)}
                                            onAssignmentClick={(assignmentId) => {
                                                if (!canWrite) return;
                                                const assignment = technicians.flatMap(t => t.assignments).find(a => a.id === assignmentId);
                                                if (assignment) handleEditAssignment(assignment);
                                            }}
                                        />
                                    ) : (
                                        <YearView
                                            currentDate={currentDate}
                                            technicians={filteredTechnicians}
                                            showWeekends={showWeekends}
                                        />
                                    )}
                                </main>

                            </div>
                        </div>
                    </>
                ) : activeTab === 'assignments' ? (
                    <AssignmentsPage
                        technicians={technicians}
                        onUpdateTechnician={handleUpdateTechnicianLocal}
                        onStartWorksite={handleStartWorksiteLocal}
                        onFinishWorksite={handleFinishWorksiteLocal}
                    />
                ) : activeTab === 'map' ? (
                    <MapPage technicians={filteredTechnicians} />
                ) : (
                    <PendingClosures
                        technicians={technicians}
                        onCloseWorksite={handleCloseWorksiteLocal}
                    />
                )}
            </div>

            {/* Standalone Assignment Creation Modal */}
            {showStandaloneAssignment && (
                <AssignmentModal
                    isOpen={showStandaloneAssignment}
                    onClose={() => setShowStandaloneAssignment(false)}
                    onSave={handleUpdateTechnicianLocal}
                    availableTechnicians={technicians.filter(t => !t.archived)}
                    services={services}
                    date={currentDate}
                />
            )}

            {/* View/Action Modal for Pending Closures or General Viewing */}
            {selectedAssignment && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-panel-theme rounded-2xl shadow-2xl border border-[var(--glass-border)]">
                        <button
                            onClick={() => setSelectedAssignmentId(null)}
                            className="absolute top-4 right-4 p-2 text-muted hover:text-main rounded-lg hover:bg-[var(--button-hover-bg)] z-10"
                        >
                            <X size={24} />
                        </button>
                        <TechnicianAssignmentView
                            assignment={selectedAssignment}
                            technicians={technicians}
                            onImagesUpdate={(newImages) => {
                                // Find tech and assignment
                                const tech = technicians.find(t => t.assignments.some(a => a.id === selectedAssignment.id));
                                if (tech) {
                                    const updatedAssignments = tech.assignments.map(a =>
                                        a.id === selectedAssignment.id
                                            ? { ...a, photos: newImages }
                                            : a
                                    );
                                    handleUpdateTechnicianLocal(tech.id, { assignments: updatedAssignments });
                                }
                            }}
                            onUpdateRemarque={(id, text) => {
                                const tech = technicians.find(t => t.assignments.some(a => a.id === id));
                                if (tech) {
                                    const updatedAssignments = tech.assignments.map(a =>
                                        a.id === id ? { ...a, remarque: text } : a
                                    );
                                    handleUpdateTechnicianLocal(tech.id, { assignments: updatedAssignments });
                                }
                            }}
                            onStartWorksite={() => handleStartWorksiteLocal(selectedAssignment.id)}
                            onFinishWorksite={() => handleFinishWorksiteLocal(selectedAssignment.id)}
                            isReadOnly={true} // Admin view usually just viewing details here
                            userRole="admin"
                        />
                    </div>
                </div>
            )}

            {/* Edit Assignment Modal */}
            {editingAssignment && (
                <EditAssignmentModal
                    isOpen={true}
                    onClose={() => setEditingAssignment(null)}
                    onSave={handleSaveEdit}
                    onDelete={handleDeleteAssignment}
                    technicians={technicians} // Pass all technicians to allow re-assignment
                    services={services}
                    initialAssignment={editingAssignment.assignment}
                    initialTechnicianIds={editingAssignment.techIds}
                />
            )}

            <DragOverlay>
                {activeDragItem ? (
                    <div
                        className={`p-2 rounded text-xs font-semibold shadow-2xl border cursor-grabbing opacity-90 scale-105 z-[9999] pointer-events-none`}
                        style={{
                            backgroundColor: (activeDragItem.assignment.color || '#3b82f6') + '30', // 30% opacity
                            borderColor: activeDragItem.assignment.color || '#3b82f6',
                            color: '#fff',
                            width: 'var(--drag-width, 100%)' // Dynamic width
                        }}
                    >
                        {activeDragItem.assignment.clientName}
                        <div className="flex gap-2 mt-1 opacity-75">
                            <span className="flex items-center gap-1"><Clock size={10} /> 1j</span>
                            <span className="flex items-center gap-1"><MapPin size={10} /> {activeDragItem.assignment.city}</span>
                        </div>
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
};
